import api from './api';
import {
  ChatMessage,
  GetMessagesResponse,
  SendMessageRequest,
  SendMessageResponse,
  AddReactionRequest,
  AddReactionResponse,
  GiphySearchResponse,
  EmojiSearchResponse,
  OnlineUsersResponse,
  SearchGifsParams,
  SearchEmojisParams,
  ChatResponse
} from '../types/Chat';
import { AxiosProgressEvent } from 'axios';

export interface UploadFileRequest {
  file: File;
  caption?: string;
  messageType?: 'image' | 'gif' | 'file';
}

export interface UploadFileResponse {
  success: boolean;
  message: ChatMessage;
  pinataUrl?: string;
  ipfsHash?: string;
}

export const chatApi = {
  // Get chat messages
  getMessages: async (params?: { limit?: number; skip?: number }): Promise<GetMessagesResponse> => {
    const response = await api.get('/api/chat/messages', { params });
    return response.data;
  },

  // Send a chat message
  sendMessage: async (data: SendMessageRequest): Promise<SendMessageResponse> => {
    const response = await api.post('/api/chat/messages', data);
    return response.data;
  },

  // Get Giphy API key
  getGiphyApiKey: async (): Promise<ChatResponse> => {
    const response = await api.get('/api/chat/giphy/key');
    return response.data;
  },

  // Search GIFs
  searchGifs: async (params: SearchGifsParams): Promise<GiphySearchResponse> => {
    const response = await api.get('/api/chat/giphy/search', { params });
    return response.data;
  },

  // Get trending GIFs
  getTrendingGifs: async (limit?: number): Promise<GiphySearchResponse> => {
    const response = await api.get('/api/chat/giphy/trending', { params: { limit } });
    return response.data;
  },

  // Get emoji API key
  getEmojiApiKey: async (): Promise<ChatResponse> => {
    const response = await api.get('/api/chat/emoji/key');
    return response.data;
  },

  // Search emojis
  searchEmojis: async (params?: SearchEmojisParams): Promise<EmojiSearchResponse> => {
    const response = await api.get('/api/chat/emoji/search', { params });
    return response.data;
  },

  // Get online users
  getOnlineUsers: async (): Promise<OnlineUsersResponse> => {
    const response = await api.get('/api/chat/users/online');
    return response.data;
  },

  // Add reaction to message
  addReaction: async (messageId: string, data: AddReactionRequest): Promise<AddReactionResponse> => {
    const response = await api.post(`/api/chat/messages/${messageId}/reaction`, data);
    return response.data;
  },

  // Delete message
  deleteMessage: async (messageId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/api/chat/messages/${messageId}`);
    return response.data;
  },

  // Test WebSocket connection
  testWebSocket: async (): Promise<{ success: boolean; message: string; wsUrl: string }> => {
    const response = await api.get('/api/chat/ws-test');
    return response.data;
  },

  //Upload file on pinata
  uploadFile: async (file: File, caption?: string, onProgress?: (progress: number) => void): Promise<UploadFileResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) {
      formData.append('caption', caption);
    }

    const response = await api.post('/api/chat/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  }
};

export default chatApi;